import { Typography } from '@mui/material';
import React, { FC, Fragment, HTMLAttributes } from 'react';

import { Tooltip } from '@sumologic/yama-ds-components';

import { RecordValue } from '../../Records';
import { getPropertyLabel } from '../../Records/Record/schema/v3/RecordOverview/RecordProperty';
import { type EntityTypeFieldsAndIndicator } from '../../Records/useEntityTypeRecordFields';
import { FIELDS_PREFIX, MatchedFavFieldValueType } from '../parsers';
import css from './FavoriteFieldProperty.module.scss';

interface FavoriteFieldPropertyProps extends HTMLAttributes<HTMLDivElement> {
  fieldName: string;
  values: MatchedFavFieldValueType[];
  entityIndicatorData?: EntityTypeFieldsAndIndicator['entityIndicatorData'];
}

export const FavoriteFieldProperty: FC<
  React.PropsWithChildren<FavoriteFieldPropertyProps>
> = ({ fieldName, values, entityIndicatorData, ...rest }) => {
  // Save the list items and account for multiple values.
  const isList = values.length > 1;
  const items = values.map(({ record, value, instanceCount }, index) => {
    const content = (
      <RecordValue
        className={css.value}
        name={fieldName}
        record={record}
        showContextAction={instanceCount === 1}
        entityIndicatorData={entityIndicatorData}
      >
        {value}
      </RecordValue>
    );

    return (
      <Fragment key={`value-${index}`}>
        {isList && (
          <li>
            {content}{' '}
            <Tooltip
              title={`Matches ${instanceCount.toLocaleString()} ${
                instanceCount === 1 ? 'Record' : 'Records'
              }`}
            >
              <span className={css.instanceCount}>
                ({instanceCount.toLocaleString()})
              </span>
            </Tooltip>
          </li>
        )}
        {!isList && content}
      </Fragment>
    );
  });

  const isExtractedField = fieldName.startsWith(FIELDS_PREFIX);
  return (
    <div {...rest}>
      <Typography variant='subtitle1'>
        {getPropertyLabel(fieldName, undefined, isExtractedField)}
      </Typography>
      <div className={css.items}>
        {isList && <ul className={css.list}>{items}</ul>}
        {!isList && items}
      </div>
    </div>
  );
};
